import React, { Fragment, useContext } from 'react';
import styles from './GroupedReportsCardTable.module.scss';
import { SFChip, SFText } from 'sfui';
import { ReportConfig, ReportValue, VersionValueType } from '../../../Models';
import { formatDate, getItemSummary } from '../../../Helpers';
import {
  Avatar,
  Customer,
  CustomerContext,
  InteractiveBox
} from 'ui-smartforce-settings';
import { StateConfigContext } from '../../../Context/StateConfigContext';
import { FullReportContext } from '../../../Context/FullReportContext';

export interface GroupedReportsCardTableProps {
  showOfficer?: boolean;
  reports: ReportValue[];
}

export const GroupedReportsCardTable = ({
  showOfficer = true,
  reports
}: GroupedReportsCardTableProps): React.ReactElement<GroupedReportsCardTableProps> => {
  const customer = useContext(CustomerContext).customer as Customer;
  const { showFullReport } = useContext(FullReportContext);
  const stateConfig = useContext(StateConfigContext)
    .stateConfig as ReportConfig;

  const onSeeFullReport = (report: ReportValue) => {
    showFullReport(report);
  };

  return (
    <>
      <div
        className={`${styles.groupedReportsCardTable} ${styles.header} ${
          !showOfficer ? styles.noOfficer : ''
        }`}
      >
        <SFText
          className={styles.officer}
          type="component-messages"
          sfColor="neutral"
        >
          OFFICER
        </SFText>

        <SFText type="component-messages" sfColor="neutral">
          TIME
        </SFText>

        <SFText
          className={styles.location}
          type="component-messages"
          sfColor="neutral"
        >
          LOCATION
        </SFText>

        <SFText
          className={styles.citizens}
          type="component-messages"
          sfColor="neutral"
        >
          CITIZENS INFORMATION
        </SFText>

        <SFText
          className={styles.officers}
          type="component-messages"
          sfColor="neutral"
        >
          OFFICERS INFORMATION
        </SFText>

        <SFText
          className={styles.tags}
          type="component-messages"
          sfColor="neutral"
        >
          TAGS
        </SFText>

        <SFText
          className={styles.duration}
          type="component-messages"
          sfColor="neutral"
        >
          DURATION
        </SFText>
      </div>

      {reports.map((r) => {
        const autor = (r.versions as VersionValueType[])[0].author;

        return (
          <InteractiveBox
            className={`${styles.groupedReportsCardTable} ${
              !showOfficer ? styles.noOfficer : ''
            } ${styles.row}`}
            key={r.id}
            onClick={() => onSeeFullReport(r)}
          >
            <div className={styles.officer}>
              <Avatar
                className={styles.avatar}
                url={autor.avatar_thumbnail_url}
                name={autor.name}
              />
              <SFText type="component-2">{autor.name}</SFText>
            </div>

            <SFText type="component-2">
              {formatDate(r.created_at as string, 'HH:mm', customer.timezone)}
            </SFText>

            <SFText className={styles.location} type="component-2">
              {r.general_information.location.address
                ? r.general_information.location.address.full
                : r.general_information.location}
            </SFText>

            <SFText className={styles.citizens} type="component-2">
              {r.citizens.map((i, index) => (
                <span key={`row-${r.id}-citizen-${index}`}>
                  {`Citizen ${index + 1} - ${getItemSummary(
                    stateConfig.citizen,
                    i
                  )}`}
                  `
                </span>
              ))}
            </SFText>

            <SFText className={styles.officers} type="component-2">
              {r.officers &&
                r.officers.map((o, index) => (
                  <span key={`row-${r.id}-officer-${index}`}>
                    {`Officer ${index + 1} - ${getItemSummary(
                      stateConfig.officer,
                      o
                    )}`}
                  </span>
                ))}
            </SFText>

            <div className={styles.tags}>
              {r.tags?.agency &&
                r.tags?.agency.map((t) => (
                  <SFChip
                    key={t.id}
                    size="small"
                    variant="outlined"
                    sfColor={t.color}
                    label={t.name}
                  />
                ))}
            </div>

            <SFText className={styles.duration} type="component-2">
              {r.general_information.duration}
            </SFText>
          </InteractiveBox>
        );
      })}
    </>
  );
};
